import { KeyboardEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import {
  AgeRanges,
  LanguageService,
  ScheduleTypes,
  SpecialExperiencesService,
  SpecialServicesService,
  SpecialSituationsService,
  TeachingSkillsService,
} from 'us-web-services';

import { Button, ButtonType } from '../../../components';
import {
  FACILITY_TYPES,
  LAST_ACCESS_OPTIONS,
  RECURRING_SEARCH_TYPE_CODES,
} from '../constants';
import { AvailabilityFilter } from './AvailabilityFilter';
import { BackgroundCheckRequest } from './BackgroundCheckRequest';
import { NameSearchFilter } from './NameSearchFilter';
import { UniversityFilter } from './UniversityFilter';

interface FacetOption {
  id: number;
  name?: string;
  label?: string;
}

const DISTANCE_OPTIONS = [
  { value: '3', label: '3 miles' },
  { value: '5', label: '5 miles' },
  { value: '10', label: '10 miles' },
  { value: '15', label: '15 miles' },
  { value: '25', label: '25 miles' },
  { value: '50', label: '50 miles' },
];

const HOURLY_RATE_OPTIONS = [
  { value: '15', label: 'Up to $15/hr' },
  { value: '20', label: 'Up to $20/hr' },
  { value: '25', label: 'Up to $25/hr' },
  { value: '30', label: 'Up to $30/hr' },
  { value: '40', label: 'Up to $40/hr' },
  { value: '60', label: 'Up to $60/hr' },
];

const MONTHLY_RATE_OPTIONS = [
  { value: '1000', label: 'Up to $1,000/mo' },
  { value: '1500', label: 'Up to $1,500/mo' },
  { value: '2000', label: 'Up to $2,000/mo' },
  { value: '2500', label: 'Up to $2,500/mo' },
  { value: '3000', label: 'Up to $3,000/mo' },
  { value: '4000', label: 'Up to $4,000/mo' },
];

interface Props {
  onFilterChange?: () => void;
  isJobInterest?: boolean;
  isDaycare?: boolean;
  verticalTypeCode?: string | null;
  searchTypeCode?: string | null;
}

export const FilterSidebar = ({
  onFilterChange,
  isJobInterest,
  isDaycare,
  verticalTypeCode,
  searchTypeCode,
}: Props): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [specialExperiences, setSpecialExperiences] = useState<FacetOption[]>([]);
  const [languages, setLanguages] = useState<FacetOption[]>([]);
  const [teachingSkills, setTeachingSkills] = useState<FacetOption[]>([]);
  const [specialSituations, setSpecialSituations] = useState<FacetOption[]>([]);
  const [specialServices, setSpecialServices] = useState<FacetOption[]>([]);

  const isRecurring =
    !!searchTypeCode && RECURRING_SEARCH_TYPE_CODES.has(searchTypeCode);
  const isHouseholdServices = verticalTypeCode === 'householdServices';
  const showChildCount = !isDaycare && !isHouseholdServices;
  const showWorksWith =
    verticalTypeCode === 'childCare' || verticalTypeCode === 'petCare';
  const showTeachingSkills = verticalTypeCode === 'childCare';
  const showSpecialSituationsAndExperienceWith =
    verticalTypeCode === 'childCare' || verticalTypeCode === 'seniorCare';
  const showRespondsAndBackground =
    !searchTypeCode || searchTypeCode === 'OCCASIONAL';

  useEffect(() => {
    void (async (): Promise<void> => {
      try {
        const response = await SpecialExperiencesService.getByFilter();
        setSpecialExperiences((response.data?.data ?? []) as FacetOption[]);
      } catch {
        setSpecialExperiences([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const response = await LanguageService.getByFilter();
        setLanguages((response.data?.data ?? []) as FacetOption[]);
      } catch {
        setLanguages([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const response = await TeachingSkillsService.getByFilter();
        setTeachingSkills((response.data?.data ?? []) as FacetOption[]);
      } catch {
        setTeachingSkills([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const response = await SpecialSituationsService.getByFilter();
        setSpecialSituations((response.data?.data ?? []) as FacetOption[]);
      } catch {
        setSpecialSituations([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const response = await SpecialServicesService.getByFilter();
        setSpecialServices((response.data?.data ?? []) as FacetOption[]);
      } catch {
        setSpecialServices([]);
      }
    })();
  }, []);

  const getBool = (key: string): boolean => searchParams.get(key) === 'true';
  const getValue = (key: string): string => searchParams.get(key) ?? '';
  const getIds = (key: string): Set<number> => {
    const raw = searchParams.get(key);
    if (!raw) return new Set();
    return new Set(
      raw
        .split(',')
        .map((s) => Number(s))
        .filter((n) => !Number.isNaN(n)),
    );
  };

  const setParam = (key: string, value: string | null): void => {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        if (value == null || value === '') next.delete(key);
        else next.set(key, value);
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
    onFilterChange?.();
  };

  const setBool = (key: string, checked: boolean): void => {
    setParam(key, checked ? 'true' : null);
  };

  const applyFacet = (key: string, ids: Set<number>): void => {
    setParam(key, ids.size ? [...ids].join(',') : null);
  };

  const clearAll = (): void => {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams();
        // Preserve the params that scope the search itself; clear everything
        // else (facets, page, etc.).
        const preserved = ['sort', 'verticalTypeId', 'verticalTypeCode', 'searchTypeCode', 'availability'];
        preserved.forEach((key) => {
          const value = sp.get(key);
          if (value) next.set(key, value);
        });
        return next;
      },
      { replace: true },
    );
    onFilterChange?.();
  };

  // Angular's sitters-search-filter-body stages location fields and only commits
  // them on "Set Location" or Enter. Matching that prevents a partial zip
  // triggering a re-query per keystroke.
  const [locDraft, setLocDraft] = useState<{
    street: string;
    city: string;
    state: string;
    zip: string;
  }>({
    street: getValue('street'),
    city: getValue('city'),
    state: getValue('state'),
    zip: getValue('zip'),
  });

  useEffect(() => {
    setLocDraft({
      street: getValue('street'),
      city: getValue('city'),
      state: getValue('state'),
      zip: getValue('zip'),
    });
    // Re-sync from URL when it changes externally (tab click, clear all, etc).

  }, [searchParams]);

  const applyLocation = (): void => {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        (['street', 'city', 'state', 'zip'] as const).forEach((k) => {
          const v = locDraft[k].trim();
          if (!v) next.delete(k);
          else next.set(k, v);
        });
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
    onFilterChange?.();
  };

  const onLocationKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') applyLocation();
  };

  const rateOptions = isDaycare ? MONTHLY_RATE_OPTIONS : HOURLY_RATE_OPTIONS;
  const rateLabel = isDaycare ? 'Maximum rate' : 'Rate per hour';

  return (
    <aside className='border border-neutral-200 rounded p-4 bg-white'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='font-semibold text-lg mb-0'>Filters</h3>
        <Button
          buttonType={ButtonType.tertiary}
          onClick={clearAll}
          className='text-sm underline text-sapphire'
        >
          Clear all
        </Button>
      </div>

      {isRecurring && !isDaycare && <AvailabilityFilter />}

      <section className='mb-4'>
        <h4 className='font-semibold mb-1'>Location</h4>
        <input
          type='tel'
          aria-label='Zip code'
          placeholder='Zip code'
          maxLength={5}
          className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm mb-2'
          value={locDraft.zip}
          onChange={(e): void =>
            setLocDraft((d) => ({ ...d, zip: e.target.value }))
          }
          onKeyUp={onLocationKeyUp}
        />
        <div className='grid grid-cols-5 gap-1 mb-2'>
          <input
            type='text'
            aria-label='City'
            placeholder='City'
            className='col-span-3 form-control border border-neutral-300 rounded px-2 py-1 text-sm'
            value={locDraft.city}
            onChange={(e): void =>
              setLocDraft((d) => ({ ...d, city: e.target.value }))
            }
            onKeyUp={onLocationKeyUp}
          />
          <input
            type='text'
            aria-label='State'
            placeholder='State'
            maxLength={2}
            className='col-span-2 form-control border border-neutral-300 rounded px-2 py-1 text-sm uppercase'
            value={locDraft.state}
            onChange={(e): void =>
              setLocDraft((d) => ({ ...d, state: e.target.value.toUpperCase() }))
            }
            onKeyUp={onLocationKeyUp}
          />
        </div>
        <input
          type='text'
          aria-label='Street'
          placeholder='Street'
          className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm mb-2'
          value={locDraft.street}
          onChange={(e): void =>
            setLocDraft((d) => ({ ...d, street: e.target.value }))
          }
          onKeyUp={onLocationKeyUp}
        />
        <Button
          buttonType={ButtonType.primary}
          id='set-location'
          onClick={applyLocation}
          className='btn btn-primary w-full'
        >
          Set Location
        </Button>
      </section>

      <section className='mb-4'>
        <h4 className='font-semibold mb-1'>Distance</h4>
        <select
          className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm'
          value={getValue('distance')}
          onChange={(e): void => setParam('distance', e.target.value)}
        >
          <option value=''>Any distance</option>
          {DISTANCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </section>

      {!isDaycare && (
        <section className='mb-4'>
          <h4 className='font-semibold mb-1'>Last active</h4>
          <select
            className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm'
            value={getValue('maxLastAccessDays')}
            onChange={(e): void => setParam('maxLastAccessDays', e.target.value)}
          >
            {LAST_ACCESS_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </section>
      )}

      {isDaycare && (
        <>
          <section className='mb-4'>
            <h4 className='font-semibold mb-1'>Type of facility</h4>
            <select
              className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm'
              value={getValue('facilityClass')}
              onChange={(e): void => setParam('facilityClass', e.target.value)}
            >
              {FACILITY_TYPES.map((f) => (
                <option key={f.value || 'all'} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </section>

          <section className='mb-4'>
            <h4 className='font-semibold mb-1'>Schedule</h4>
            <select
              className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm'
              value={getValue('scheduleTypes')}
              onChange={(e): void => setParam('scheduleTypes', e.target.value)}
            >
              {ScheduleTypes.map((s) => (
                <option key={s.id ?? 'all'} value={s.id == null ? '' : String(s.id)}>
                  {s.label}
                </option>
              ))}
            </select>
          </section>

          <AgeRangeFilter
            selected={getIds('ageRanges')}
            onApply={(ids: Set<number>): void => applyFacet('ageRanges', ids)}
          />
        </>
      )}

      <section className='mb-4'>
        <h4 className='font-semibold mb-1'>{rateLabel}</h4>
        <select
          className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm'
          value={getValue('maximumRate')}
          onChange={(e): void => setParam('maximumRate', e.target.value)}
        >
          <option value=''>Any rate</option>
          {rateOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </section>

      {!isJobInterest && showChildCount && (
        <section className='mb-4'>
          <h4 className='font-semibold mb-1'>Show rates for</h4>
          <select
            className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm'
            value={getValue('numberOfChildren')}
            onChange={(e): void => setParam('numberOfChildren', e.target.value)}
          >
            <option value=''>Any</option>
            <option value='1'>1 child</option>
            <option value='2'>2 children</option>
            <option value='3'>3 children</option>
            <option value='4'>4 children</option>
          </select>
        </section>
      )}

      {showRespondsAndBackground && (
        <section className='mb-4'>
          <h4 className='font-semibold mb-2'>Trust &amp; safety</h4>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={getBool('backgroundCheck')}
              onChange={(e): void => setBool('backgroundCheck', e.target.checked)}
            />
            Completed background check
          </label>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={searchParams.get('maxResponseTime') === '1'}
              onChange={(e): void => setParam('maxResponseTime', e.target.checked ? '1' : null)}
            />
            Responds quickly
          </label>
        </section>
      )}

      {!isDaycare && (
        <section className='mb-4'>
          <h4 className='font-semibold mb-2'>Transportation</h4>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={getBool('hasCar')}
              onChange={(e): void => setBool('hasCar', e.target.checked)}
            />
            Has a car
          </label>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={getBool('willingToDriveChildren')}
              onChange={(e): void => setBool('willingToDriveChildren', e.target.checked)}
            />
            Willing to drive children
          </label>
        </section>
      )}

      {showWorksWith && (
        <section className='mb-4'>
          <h4 className='font-semibold mb-2'>Works with</h4>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={getBool('willSitDog')}
              onChange={(e): void => setBool('willSitDog', e.target.checked)}
            />
            Dogs
          </label>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={getBool('willSitCat')}
              onChange={(e): void => setBool('willSitCat', e.target.checked)}
            />
            Cats
          </label>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={getBool('willSitOtherPet')}
              onChange={(e): void => setBool('willSitOtherPet', e.target.checked)}
            />
            Other pets
          </label>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={getBool('willSitSenior')}
              onChange={(e): void => setBool('willSitSenior', e.target.checked)}
            />
            Seniors
          </label>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={getBool('willSitHome')}
              onChange={(e): void => setBool('willSitHome', e.target.checked)}
            />
            Household services
          </label>
        </section>
      )}

      {!isDaycare && (
        <section className='mb-4'>
          <h4 className='font-semibold mb-2'>Preferences</h4>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={getBool('acceptsCreditCard')}
              onChange={(e): void => setBool('acceptsCreditCard', e.target.checked)}
            />
            Accepts credit card
          </label>
          <label className='flex items-start gap-2 mb-1 text-sm'>
            <input
              type='checkbox'
              checked={getBool('excludeFavorites')}
              onChange={(e): void => setBool('excludeFavorites', e.target.checked)}
            />
            Exclude my favorites
          </label>
        </section>
      )}

      {!isDaycare && (
        <>
          <Facet
            title='Languages:'
            urlKey='languages'
            options={languages}
            getIds={getIds}
            onApply={applyFacet}
          />
          {showTeachingSkills && (
            <Facet
              title='Teaching Skills:'
              urlKey='teachingSkillIds'
              options={teachingSkills}
              getIds={getIds}
              onApply={applyFacet}
            />
          )}
          {showSpecialSituationsAndExperienceWith && (
            <Facet
              title='Special Situations:'
              urlKey='specialSituationIds'
              options={specialSituations}
              getIds={getIds}
              onApply={applyFacet}
            />
          )}
          <Facet
            title='Willing to help with:'
            urlKey='specialServiceIds'
            options={specialServices}
            getIds={getIds}
            onApply={applyFacet}
          />
          {showSpecialSituationsAndExperienceWith && (
            <Facet
              title='Experience with:'
              urlKey='certificationIds'
              options={specialExperiences}
              getIds={getIds}
              onApply={applyFacet}
            />
          )}
          <UniversityFilter />
          <NameSearchFilter />
        </>
      )}

      {isRecurring && !isDaycare && <BackgroundCheckRequest />}
    </aside>
  );
};

interface FacetProps {
  title: string;
  urlKey: string;
  options: FacetOption[];
  getIds: (key: string) => Set<number>;
  onApply: (key: string, ids: Set<number>) => void;
}

// Collapsible checkbox section. Click the header to expand/collapse; toggling
// any checkbox applies the filter immediately — no separate Apply button, to
// match the other inline filter sections above (Trust & safety, Works with).
const Facet = ({ title, urlKey, options, getIds, onApply }: FacetProps): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const committed = getIds(urlKey);

  const toggle = (id: number): void => {
    const next = new Set(committed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onApply(urlKey, next);
  };

  const clear = (): void => {
    onApply(urlKey, new Set());
  };

  return (
    <section className='border-t border-neutral-200 pt-3 mb-3'>
      <Button
        buttonType={ButtonType.tertiary}
        aria-expanded={open}
        className='w-full flex justify-between items-center font-semibold bg-transparent border-0 p-0 cursor-pointer text-left'
        onClick={(): void => setOpen((v) => !v)}
      >
        <span>
          {title}
          {committed.size > 0 && (
            <span className='ml-1 text-sm text-sapphire'>({committed.size})</span>
          )}
        </span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className='text-neutral-500 text-sm' />
      </Button>
      {open && (
        <div className='mt-2'>
          {options.length === 0 ? (
            <p className='text-sm text-neutral-500'>{t('common.loading')}</p>
          ) : (
            <div className='space-y-1'>
              {options.map((o) => (
                <label key={o.id} className='flex items-start gap-2 text-sm'>
                  <input
                    type='checkbox'
                    checked={committed.has(o.id)}
                    onChange={(): void => toggle(o.id)}
                  />
                  <span>{o.name ?? o.label}</span>
                </label>
              ))}
            </div>
          )}
          {committed.size > 0 && (
            <Button
              buttonType={ButtonType.tertiary}
              onClick={clear}
              className='mt-2 text-sm underline text-sapphire bg-transparent border-0 p-0'
            >
              Clear
            </Button>
          )}
        </div>
      )}
    </section>
  );
};

interface AgeRangeFilterProps {
  selected: Set<number>;
  onApply: (ids: Set<number>) => void;
}

// Angular uses a comma-separated `ageRanges` param (ids from AgeRanges). This
// renders the same AgeRanges array from us-web-services as a multi-select
// dropdown so parents can pick one or more age bands.
const AgeRangeFilter = ({ selected, onApply }: AgeRangeFilterProps): JSX.Element => {
  const toggle = (id: number): void => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onApply(next);
  };

  return (
    <section className='mb-4'>
      <h4 className='font-semibold mb-1'>Age range</h4>
      <div className='space-y-1'>
        {AgeRanges.filter((r) => r.id != null).map((r) => (
          <label key={r.id} className='flex items-center gap-2 text-sm'>
            <input
              type='checkbox'
              checked={selected.has(r.id as number)}
              onChange={(): void => toggle(r.id as number)}
            />
            <span>{r.label}</span>
          </label>
        ))}
      </div>
    </section>
  );
};
